import GapPolicyToggle from '../GapPolicyToggle';
import ShapefilePreview from '../ShapefilePreview';

import { VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import DropZone from 'components/DropZone';

import {
    CANCEL_BUTTON_TEST_ID,
    CONTINUE_BUTTON_TEST_ID,
    ERROR_BANNER_TEST_ID,
    TEST_ID
} from './constants';
import type { FunctionComponent, Props, SectionHeaderProps } from './types';

import styles from './styles.module.scss';

const SectionHeader = ({ eyebrow, hint, title }: SectionHeaderProps) => (
    <VerticalContainer className={styles.sectionHeader}>
        <Wrapper className={styles.sectionEyebrow}>{eyebrow}</Wrapper>

        <Wrapper className={styles.sectionTitle}>{title}</Wrapper>

        {hint && <Wrapper className={styles.sectionHint}>{hint}</Wrapper>}
    </VerticalContainer>
);

const UploadCard: FunctionComponent<Props> = ({
    acceptFileTypes,
    approxAreaHectares,
    className = '',
    errorMessage,
    fileName,
    gapPolicy,
    isFilled,
    isProcessing,
    isSubmitting,
    labels,
    onCancel,
    onFilesSelected,
    onGapPolicyChange,
    onReplaceFile,
    onSubmit,
    rateColumn,
    rateUnit,
    testId = TEST_ID,
    zones
}) => {
    const step1Title = isFilled ? labels.section1FilledTitle : labels.section1EmptyTitle;
    const step1Hint = isFilled ? undefined : labels.section1EmptyHint;
    const dropZoneCta = isProcessing ? labels.dropZoneCtaProcessing : labels.dropZoneCta;
    const cancelText = isFilled ? labels.replaceFile : labels.cancel;
    const cancelHandler = isFilled ? onReplaceFile : onCancel;
    const continueText = isSubmitting ? labels.submittingCta : labels.continueCta;
    const continueDisabled = !isFilled || isSubmitting;
    const continueDisabledClass = continueDisabled ? styles.buttonPrimaryDisabled : '';
    const handleContinueClick = continueDisabled ? undefined : onSubmit;
    const step1Body = isFilled ? (
        <ShapefilePreview
            approxAreaLabel={labels.approxArea}
            areaValueText={labels.previewAreaValue(approxAreaHectares)}
            crsLabel={labels.crs}
            crsValueFormatter={labels.crsValue}
            fileLabel={labels.file}
            fileName={fileName}
            rateColumn={rateColumn}
            rateColumnLabel={labels.rateColumn}
            rateRangeLabel={labels.rateRange}
            rateSingleFormatter={labels.rateSingle}
            rateSpanFormatter={labels.rateSpan}
            rateUnit={rateUnit}
            zoneCountLabel={labels.zones}
            zones={zones}
        />
    ) : (
        <DropZone
            acceptFileTypes={acceptFileTypes}
            ctaText={dropZoneCta}
            formatsText={labels.dropZoneFormats}
            isProcessing={isProcessing}
            onFilesSelected={onFilesSelected}
        />
    );

    return (
        <VerticalContainer className={`${className} ${styles.uploadCard}`} testId={testId}>
            {errorMessage && <Wrapper testId={ERROR_BANNER_TEST_ID}>{errorMessage}</Wrapper>}

            <VerticalContainer className={styles.section}>
                <SectionHeader
                    eyebrow={labels.sectionEyebrowStep1}
                    hint={step1Hint}
                    title={step1Title}
                />

                {step1Body}
            </VerticalContainer>

            <VerticalContainer className={styles.sectionWithDivider}>
                <SectionHeader
                    eyebrow={labels.section2Eyebrow}
                    hint={labels.section2Hint}
                    title={labels.section2Title}
                />

                <GapPolicyToggle
                    baseRateLabel={labels.gapPolicyBaseRate}
                    gapPolicy={gapPolicy}
                    maskLabel={labels.gapPolicyMask}
                    onGapPolicyChange={onGapPolicyChange}
                />
            </VerticalContainer>

            <Wrapper className={styles.buttonRow}>
                <Wrapper
                    className={styles.buttonSecondary}
                    onClick={cancelHandler}
                    testId={CANCEL_BUTTON_TEST_ID}>
                    {cancelText}
                </Wrapper>

                <Wrapper
                    className={`${styles.buttonPrimary} ${continueDisabledClass}`}
                    onClick={handleContinueClick}
                    testId={CONTINUE_BUTTON_TEST_ID}>
                    {continueText}
                </Wrapper>
            </Wrapper>
        </VerticalContainer>
    );
};

export default UploadCard;
